import { DOCUMENT } from "@angular/common";
import { Component, EventEmitter, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, Output, Renderer2 } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { SimpleFonoArea, SimpleFonoCallResponse, SimpleFonoService } from "src/app/services/simple-fono.service";

interface SimpleFonoStatusPayload {
	status?: string;
	state?: string;
}

@Component({
	selector: "app-simple-fono-modal",
	templateUrl: "./simple-fono-modal.component.html",
	styleUrls: ["./simple-fono-modal.component.scss"],
})
export class SimpleFonoModalComponent implements OnInit, OnDestroy {
	@Input() origin: string = "SimpleFactura.App.Subheader.HablemosPorFono";
	@Input() showAreas: boolean = false;
	@Input() fixedAreaCode: string = "soporte";
	@Output() close = new EventEmitter<void>();

	public name: string = "";
	public phone: string = "";
	public selectedAreaCode: string = "";
	public areas: SimpleFonoArea[] = [];
	public loadingAreas: boolean = false;
	public loadingCallRequest: boolean = false;
	public loadingRatingRequest: boolean = false;
	public showFeedback: boolean = false;
	public showStatus: boolean = false;
	public statusMessage: string = "";
	public errorMessage: string = "";
	public ratingMessage: string = "Despues de terminar la llamada podras evaluarnos.";
	public rating: number = 0;
	public comment: string = "";
	public ratingEnabled: boolean = false;

	private readonly stateMessages: { [key: string]: string } = {
		Created: "Hemos recibido tu solicitud, en breve te contactamos.",
		Queued: "Tu solicitud esta en cola, pronto un agente estara disponible.",
		Processing: "Estamos procesando tu solicitud...",
		CallsCreated: "Buscando un agente para tu solicitud...",
		CallingClient: "Atento. Te estamos llamando ahora.",
		Completed: "Gracias por llamar a ChileSystems.",
		Error: "Ocurrio un error. Por favor intentalo nuevamente.",
		Retry: "No pudimos contactarnos contigo, te llamaremos en cuanto tengamos disponibilidad.",
	};

	private token: string | null = null;
	private fromPhoneNumber: string | null = null;
	private hubConnection: signalR.HubConnection | null = null;
	private closeTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private renderer: Renderer2,
		private zone: NgZone,
		private simpleFonoService: SimpleFonoService
	) {}

	ngOnInit(): void {
		this.renderer.addClass(this.document.body, "modal-open");

		if (this.showAreas) {
			this.loadAreas();
		}
	}

	ngOnDestroy(): void {
		this.renderer.removeClass(this.document.body, "modal-open");
		this.clearCloseTimer();
		this.stopHubConnection();
	}

	@HostListener("document:keydown.escape", ["$event"])
	onEscape(event: KeyboardEvent): void {
		event.preventDefault();
		this.closeModal();
	}

	onPhoneChange(value: string): void {
		this.phone = (value || "").replace(/\D/g, "").slice(0, 8);
	}

	async submitCall(): Promise<void> {
		if (this.loadingCallRequest) {
			return;
		}

		const trimmedName = this.name.trim();
		const rawPhone = this.phone.trim();
		const areaCode = this.showAreas ? this.selectedAreaCode : this.fixedAreaCode;

		this.errorMessage = "";

		if (!trimmedName) {
			this.errorMessage = "El nombre es obligatorio.";
			return;
		}

		if (rawPhone.length !== 8) {
			this.errorMessage = "El telefono debe tener 8 digitos.";
			return;
		}

		if (this.showAreas && !areaCode) {
			this.errorMessage = "Debes seleccionar un area.";
			return;
		}

		this.loadingCallRequest = true;
		this.setStatus("Enviando...");

		try {
			const response = await this.simpleFonoService.requestCallLikeRazor({
				name: trimmedName,
				phoneNumber: "+569" + rawPhone,
				origin: this.origin,
				areaCode: areaCode || "",
			});

			await this.zone.run(async () => {
				const data: SimpleFonoCallResponse | null = response.data;

				if (response.status === 409) {
					this.errorMessage = "Ya existe una solicitud de llamada para este numero.";
					this.loadingCallRequest = false;
					return;
				}

				if (data?.isError) {
					this.errorMessage = data.error?.description || "Ocurrio un error. Favor intentar nuevamente.";
					this.loadingCallRequest = false;
					return;
				}

				if (!response.ok) {
					this.errorMessage = "Error inesperado (" + response.status + "). Favor intentar nuevamente.";
					this.loadingCallRequest = false;
					return;
				}

				this.token = data?.token || null;
				this.fromPhoneNumber = data?.fromPhoneNumber || null;
				this.showFeedback = true;
				this.ratingEnabled = false;
				this.ratingMessage = "Despues de terminar la llamada podras evaluarnos.";
				this.setStatus("Created");

				await this.connectHub();
			});
		} catch (error) {
			this.zone.run(() => {
				this.errorMessage = "Error de red. Favor intentar nuevamente.";
				this.loadingCallRequest = false;
			});
		}
	}

	setRating(rating: number): void {
		if (!this.ratingEnabled || this.loadingRatingRequest) {
			return;
		}

		this.rating = rating;
	}

	submitRating(): void {
		if (!this.token || !this.rating || !this.ratingEnabled || this.loadingRatingRequest) {
			return;
		}

		this.loadingRatingRequest = true;
		this.errorMessage = "";
		this.setStatus("Enviando evaluacion...");

		this.simpleFonoService
			.rateCall(this.token, {
				rating: this.rating,
				comment: this.comment.trim(),
			})
			.subscribe({
				next: () => {
					this.loadingRatingRequest = false;
					this.ratingEnabled = false;
					this.setStatus("Gracias por tu calificacion.");
					this.clearCloseTimer();
					this.closeTimer = setTimeout(() => this.closeModal(), 2000);
				},
				error: () => {
					this.loadingRatingRequest = false;
					this.errorMessage = "No se pudo enviar tu evaluacion.";
				},
			});
	}

	closeModal(): void {
		this.close.emit();
	}

	private loadAreas(): void {
		this.loadingAreas = true;
		this.errorMessage = "";

		this.simpleFonoService.getAreas().subscribe({
			next: (areas: SimpleFonoArea[]) => {
				this.areas = areas || [];
				this.loadingAreas = false;
			},
			error: () => {
				this.loadingAreas = false;
				this.errorMessage = "No se pudo cargar las areas. Favor intente nuevamente.";
			},
		});
	}

	private async connectHub(): Promise<void> {
		if (!this.token) {
			this.loadingCallRequest = false;
			this.errorMessage = "No se pudo iniciar el seguimiento de la llamada.";
			return;
		}

		this.stopHubConnection();

		this.hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(this.simpleFonoService.getHubUrl(), {
				accessTokenFactory: () => this.token || "",
			})
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Warning)
			.build();

		this.hubConnection.on("StatusChanged", (payload: SimpleFonoStatusPayload) => {
			this.zone.run(() => {
				this.setStatus(payload?.status || payload?.state || JSON.stringify(payload));
			});
		});

		this.hubConnection.on("JobCompleted", () => {
			this.zone.run(() => {
				this.setStatus("Completed");
				this.ratingEnabled = true;
				this.ratingMessage = "Ya puedes evaluarnos.";
			});
		});

		this.hubConnection.onclose((error?: Error) => {
			this.zone.run(() => {
				if (error) {
					this.setStatus("Conexion perdida");
				}
			});
		});

		this.hubConnection.onreconnected(() => {
			this.hubConnection?.invoke("JoinJob").catch(() => {});
		});

		try {
			await this.hubConnection.start();
			this.zone.run(() => {
				this.loadingCallRequest = false;
				this.setStatus("Conectado. Te llamaremos pronto.");
			});
			await this.hubConnection.invoke("JoinJob");
		} catch (error) {
			this.zone.run(() => {
				this.loadingCallRequest = false;
				this.errorMessage = "No se pudo conectar con el servidor de notificaciones. Intenta nuevamente.";
			});
		}
	}

	private setStatus(statusOrMessage: string): void {
		if (!statusOrMessage) {
			this.showStatus = false;
			this.statusMessage = "";
			return;
		}

		if (statusOrMessage === "CallingClient") {
			this.statusMessage = this.fromPhoneNumber ? "Atento. Te estamos llamando ahora desde el numero " + this.fromPhoneNumber + "." : "Atento. Te estamos llamando ahora.";
		} else {
			this.statusMessage = this.stateMessages[statusOrMessage] || statusOrMessage;
		}

		this.showStatus = true;
	}

	private stopHubConnection(): void {
		const connection = this.hubConnection;
		this.hubConnection = null;

		if (connection) {
			connection.stop().catch(() => {});
		}
	}

	private clearCloseTimer(): void {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
	}
}
